import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, CircleAlert, ArrowUpRight } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { waLink, mailtoLink } from "@/data/site";
import { PageHeader } from "@/components/site/PageHeader";
import { SSButton } from "@/components/site/SSButton";

const DOCUMENTS = [
  { key: "sale_deed", label: "Sale deed / title document", why: "Starting point for ownership and title-chain review." },
  { key: "khasra", label: "Khasra / Khatoni or relevant revenue record", why: "Helps identify the recorded parcel, tenure and revenue particulars." },
  { key: "mutation", label: "Mutation record", why: "Useful supporting revenue record; not treated here as a substitute for title review." },
  { key: "ec", label: "Encumbrance Certificate / search material", why: "Used as part of checking registered encumbrances; scope and period still need review." },
  { key: "map", label: "Site / revenue map", why: "Helps reconcile the documentary parcel with location, boundaries and access." },
  { key: "layout", label: "Approved layout / plotting approval", why: "Especially relevant where a plotted development or subdivision is being sold." },
  { key: "sanction", label: "Sanctioned building plan", why: "Relevant for an existing house/building and comparison with what exists on site." },
  { key: "rera", label: "RERA registration / project details (if applicable)", why: "Relevant for projects that fall within the applicable RERA framework." },
  { key: "dues", label: "Tax, utility and other dues records", why: "Helps surface outstanding property-related liabilities requiring confirmation." },
  { key: "agreement", label: "Agreement / token receipt / payment document", why: "Important if money has already been paid or terms have been committed." },
];

export default function DocumentIntake() {
  useSeo({
    title: "Property Document Check | Buyer Assistance",
    description: "Build a preliminary Dehradun and Uttarakhand property-document checklist before committing: title, revenue records, maps, approvals, encumbrance and building documents.",
    path: "/buyer-assistance/documents",
  });
  const [state, setState] = useState({});
  const setStatus = (key, value) => setState((s) => ({ ...s, [key]: value }));
  const counts = useMemo(() => {
    const vals = Object.values(state);
    return {
      have: vals.filter((v) => v === "have").length,
      missing: vals.filter((v) => v === "missing").length,
      unsure: vals.filter((v) => v === "unsure").length,
    };
  }, [state]);
  const summary = DOCUMENTS.map((d) => state[d.key] ? `${d.label}: ${state[d.key] === "have" ? "I have it" : state[d.key] === "missing" ? "Not available" : "Not sure"}` : null).filter(Boolean).join("; ");
  const msg = `Hi Suhail, I used the Selling Sunsets property-document check. ${summary || "I need help understanding which property documents I should obtain."}. I would like a preliminary buyer-side review.`;

  return (
    <>
      <PageHeader
        eyebrow="Buyer Assistance"
        marker="SS·DOC"
        title="What documents do you actually have?"
        intro="A document can exist without answering the question you need answered. Use this preliminary inventory to organise what you have, identify obvious gaps and decide what needs professional verification before you commit."
        breadcrumbs={[{ label: "Buyer Assistance", to: "/buyer-assistance" }, { label: "Document Check" }]}
      />
      <section className="ss-container pb-28">
        <div className="grid lg:grid-cols-[1.35fr_.65fr] gap-10 lg:gap-16 items-start">
          <div className="space-y-3">
            {DOCUMENTS.map((d, i) => (
              <div key={d.key} className="border border-[#E2DDD5] bg-[#F2EFE9] p-5 sm:p-6">
                <div className="flex gap-4">
                  <span className="font-mono-ss text-[0.68rem] text-[#C2A374] mt-1">{String(i + 1).padStart(2,"0")}</span>
                  <div className="flex-1">
                    <h2 className="font-display text-xl sm:text-2xl font-normal text-[#1A1C1E]">{d.label}</h2>
                    <p className="mt-1.5 text-[0.88rem] font-light text-[#52575D]">{d.why}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {[["have","I have it"],["missing","Not available"],["unsure","Not sure"]].map(([v,l]) => (
                        <button key={v} onClick={() => setStatus(d.key,v)} className={`border px-3 py-2 text-[0.72rem] uppercase tracking-[0.08em] transition-colors ${state[d.key]===v ? "bg-[#1A1C1E] border-[#1A1C1E] text-[#F7F5F0]" : "border-[#C8C2B7] hover:border-[#1A1C1E]"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <aside className="lg:sticky lg:top-28 border border-[#2D3B32]/20 bg-[#EFECE6] p-7 sm:p-8">
            <p className="ss-overline text-[#8C6A53]">Your preliminary inventory</p>
            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
              <div className="border border-[#D7D1C7] p-3"><strong className="font-display text-2xl font-light block">{counts.have}</strong><span className="text-[0.65rem] uppercase tracking-wider">Have</span></div>
              <div className="border border-[#D7D1C7] p-3"><strong className="font-display text-2xl font-light block">{counts.missing}</strong><span className="text-[0.65rem] uppercase tracking-wider">Missing</span></div>
              <div className="border border-[#D7D1C7] p-3"><strong className="font-display text-2xl font-light block">{counts.unsure}</strong><span className="text-[0.65rem] uppercase tracking-wider">Unsure</span></div>
            </div>
            <div className="mt-7 flex gap-3">
              <CircleAlert className="h-5 w-5 shrink-0 text-[#8C6A53]" strokeWidth={1.5}/>
              <p className="text-[0.82rem] leading-relaxed text-[#52575D]">This is an intake tool, not document authentication, title certification or legal clearance. A document marked “I have it” may still be incomplete, outdated, inconsistent or require verification against official records.</p>
            </div>
            <div className="mt-7 flex flex-col gap-3">
              <SSButton href={waLink(msg)} variant="primary" arrow={false}>Send this context on WhatsApp</SSButton>
              <SSButton href={mailtoLink("Property document review — Selling Sunsets", msg)} variant="secondary" arrow={false}>Email my review request</SSButton>
              <Link to="/advisory" className="mt-1 inline-flex items-center gap-1.5 text-[0.74rem] uppercase tracking-[0.1em]"><span className="ss-link">Request buyer assistance</span><ArrowUpRight className="h-4 w-4" strokeWidth={1.5}/></Link>
            </div>
          </aside>
        </div>
        <div className="mt-12 border-t border-[#E2DDD5] pt-9 max-w-3xl">
          <div className="flex gap-3"><Check className="h-5 w-5 text-[#2D3B32] shrink-0" strokeWidth={1.5}/><p className="text-[0.9rem] font-light text-[#52575D]">For privacy, this page does not upload or store your deeds or identity documents. Start with the inventory. Sensitive documents can be exchanged only through an agreed private review process.</p></div>
        </div>
      </section>
    </>
  );
}
